import {
  CloseOutlined,
  PrinterOutlined,
  SyncOutlined,
} from "@ant-design/icons";
import {
  Button,
  Flex,
  Form,
  message,
  Modal,
  Space,
  Spin,
  Typography,
} from "antd";
import { useEffect, useRef, useState } from "react";
import { ReadyToPrint } from "../../components/Printer/ReadyToPrint";
import { services as api } from "../../services";
import { SessionPrescription } from "../../components/Printer/PrescribeMedicines/SessionPrescription";
import { PrintPrescribeMedicine } from "../../components/Printer/PrescribeMedicines/PrintPrescribeMedicine";
import { NewPrescription } from "../../components/Printer/PrescribeMedicines/NewPrescription";
import { sessionapi } from "../../services/sessionapi";

const { Title } = Typography;

const PrintForm = ({ state, medicinesLookup, sessions }) => {
  return (
    // <PrintPrescribeMedicine state={state} medicinesLookup={medicinesLookup} />
    <NewPrescription
      state={state}
      medicinesLookup={medicinesLookup}
      sessions={sessions}
    />
  );
};

const ClientPrescriptionModal = ({ visible, onClose, collectIds }) => {
  const [form] = Form.useForm();
  const [medicinesLookup, setMedicinesLookup] = useState([]);
  const [state, setState] = useState();
  const [loading, setLoading] = useState(false);
  const [medloading, setMedLoading] = useState(false);
  const [sessions, setSessions] = useState([]);
  const printRef = useRef();
  const [saveLoading, setSaveLoading] = useState(false);
  // template medicine lookups api
  const getTempMedicineLookUp = async () => {
    const result = await api?.sessionapi?.getTemplateMedicineLookUp(
      collectIds?.tempId,
    );
    const formatted = result?.map((item) => ({
      id: item.id,
      name: item.name,
      strength: item.strength || "",
      intake_method: item.intake_method || "",
      duration: item?.duration || "",
    }));
    setMedicinesLookup(formatted);
  };
  //   client session detail api
  const getClientSessionDetails = async () => {
    const result = await api?.sessionapi?.getClientSessionDetail(
      collectIds?.sessionId,
    );
    const client = result?.appointment?.client_info;
    setState((prev) => ({
      ...prev,
      ...result,
      name: client?.name,
      age: client?.age,
    }));
  };

  //   view session prescription api
  const getViewSessionPrescription = async () => {
    setMedLoading(true);
    try {
      const result = await api?.sessionapi?.getViewSessionPrescription({
        id: collectIds?.sessionId,
        templateID: collectIds?.tempId,
      });
      const existingMedicines = result?.medicences?.map((med) => ({
        id: med?.medicineID || `custom-${med.id}`,
        medicineID: med?.medicineID,
        name: med?.medicineName,
        medicineName: med?.medicineName || "",
        dosage: med?.dosage || "",
        duration: med?.duration || "",
        notes: med?.notes || "",
        isCustom: !med?.medicineID,
      }));

      setState((prev) => ({
        ...prev,
        ...result,
        prescriptionId: result?.id ?? null,
        medicines: existingMedicines ?? [],
      }));
      const selectedMeds = result?.medicences
        ?.filter((med) => med?.medicineID)
        ?.map((med) => ({
          value: med?.medicineID,
          label: med?.medicineName,
        }));
      form.setFieldsValue({
        medicines: selectedMeds?.map((m) => m.value),
      });
      setMedicinesLookup((prev) => {
        const existingIds = prev.map((p) => p.id);
        const newOnes = (selectedMeds || [])
          .filter((m) => !existingIds.includes(m.value))
          .map((m) => ({ id: m.value, name: m.label }));
        return [...prev, ...newOnes];
      });
    } catch (error) {
      console.error(error);
      message.error("Failed to view session prescription");
    } finally {
      setMedLoading(false);
    }
  };

  useEffect(() => {
    if (api && visible) {
      getTempMedicineLookUp();
      getClientSessionDetails();
      getViewSessionPrescription();
      getSessionsById();
    }
  }, [api, visible]);

  const SaveUpdateSessionPrescription = async () => {
    try {
      const medicines = state?.medicines || [];

      if (!medicines.length) {
        message.warning("Please select at least one medicine.");
        return;
      }
      const payload = {
        medicences: state?.medicines?.map((med) => ({
          medicineID: med.isCustom ? null : med.id,
          medicineName: med.medicineName || med.name || "",
          // dosage: med.dosage || "",
          // duration: med.duration || "",
          notes: med.notes || "",
        })),
        IsActive: true,
      };
      setLoading(true);
      const result = await api?.sessionapi?.addEditSessionTemplatePrescription(
        payload,
        collectIds.tempId,
        collectIds.sessionId,
        state?.prescriptionId,
      );

      if (result?.success) {
        message.success(
          `Session Prescription ${state?.prescriptionId ? "updated" : "saved"} successfully!`, // ✅
        );
        onClose();
      } else {
        message.error(
          result?.message ||
            `Failed to ${state?.prescriptionId ? "update" : "save"} prescription`, // ✅
        );
      }
    } catch (error) {
      console.error(error);
      message.error("An unexpected error occurred while saving prescription.");
    } finally {
      setLoading(false);
    }
  };

  const SaveSessionPrescription = async () => {
    try {
      const medicines = state?.medicines?.filter((med) => med.isNew) || [];

      if (!medicines.length) {
        message.warning("Please select at least one medicine.");
        return;
      }
      const payload = {
        medicences: medicines?.map((med) => ({
          medicineID: med.isCustom ? null : med.id,
          medicineName: med.medicineName || med.name || "",
          notes: med.notes || "",
        })),
        IsActive: true,
      };
      setSaveLoading(true);
      const result = await api?.sessionapi?.addSessionTemplatePrescription(
        payload,
        collectIds.tempId,
        collectIds.sessionId,
      );

      if (result?.success) {
        message.success(`Session Prescription saved successfully!`);
        onClose();
      } else {
        message.error(result?.message || `Failed to save prescription`);
      }
    } catch (error) {
      console.error(error);
      message.error("An unexpected error occurred while saving prescription.");
    } finally {
      setSaveLoading(false);
    }
  };

  const handleChange = (field, value, index) => {
    if (field === "medicines") {
      setState((prev) => {
        const prevMedicines = prev?.medicines || [];

        const customMedicines = prevMedicines.filter((m) => m.isCustom);

        const selected = value.map((v) => {
          const id = v.value;

          const existing = prevMedicines.find((m) => m.id === id);

          const lookupMedicine = medicinesLookup.find((m) => m.id === id);

          return existing
            ? { ...existing }
            : {
                id,
                name: lookupMedicine?.name,
                medicineName: [lookupMedicine?.name, lookupMedicine?.strength]
                  .filter(Boolean)
                  .join(" "),
                notes: [lookupMedicine?.intake_method, lookupMedicine?.duration]
                  .filter(Boolean)
                  .join(" "),
                isCustom: false,
                isNew: true,
              };
        });

        return {
          ...prev,
          medicines: [
            ...selected,
            ...customMedicines.filter(
              (c) => !selected.find((s) => s.id === c.id),
            ),
          ],
        };
      });
    } else {
      setState((prev) => {
        const medicines = [...(prev?.medicines || [])];

        medicines[index] = {
          ...medicines[index],
          [field]: value,
        };

        return {
          ...prev,
          medicines,
        };
      });
    }
  };
  const handleModalClose = () => {
    onClose();
    form.setFieldsValue({
      medicines: null,
    });
    setState(null);
  };

  const handleAddCustomMedicine = () => {
    setState((prev) => ({
      ...prev,
      medicines: [
        ...(prev?.medicines || []),
        {
          id: `custom-${Date.now()}`,
          medicineName: "",
          notes: "",
          isCustom: true,
          isNew: true,
        },
      ],
    }));
  };

  const handleRemoveMedicine = (id) => {
    setState((prev) => ({
      ...prev,
      medicines: prev?.medicines?.filter((m) => m.id !== id),
    }));
  };

  const getSessionsById = async () => {
    try {
      const result = await sessionapi?.getAllSessionsDateTime(
        collectIds?.sessionId,
      );
      if (result) {
        setSessions(result);
      }
    } catch (error) {
      console.log(error);
    }
  };
  return (
    <Modal
      title={
        <Flex className="w-100" justify="space-between" align="center">
          <Title level={5} style={{ margin: 0 }}>
            Prescription Medicines
          </Title>
          <Button
            type="text"
            className="bg-transparent p-0"
            onClick={handleModalClose}
          >
            <CloseOutlined />
          </Button>
        </Flex>
      }
      open={visible}
      onCancel={handleModalClose}
      width={800}
      centered
      closeIcon={null}
      footer={
        <Flex
          justify={state?.prescriptionId ? "space-between" : "end"}
          align="center"
          gap={10}
        >
          {state?.prescriptionId && (
            <Button
              type="default"
              onClick={SaveSessionPrescription}
              loading={saveLoading}
            >
              Save Prescribed Medicines
            </Button>
          )}
          <Space>
            <Button
              style={{ background: "#2F80ED" }}
              className="text-white border-0"
              disabled={!state?.medicines?.length}
              icon={<PrinterOutlined />}
              onClick={() => {
                printRef.current?.(); // safely call print
              }}
            >
              Take a Print
            </Button>
            <Button
              type="primary"
              onClick={SaveUpdateSessionPrescription}
              loading={loading}
            >
              {state?.prescriptionId ? "Update" : "Save"} Prescribed Medicines
            </Button>
          </Space>
        </Flex>
      }
    >
      {medloading ? (
        <Flex justify="center" align="center" style={{ height: "200px" }}>
          <Spin
            size="large"
            indicator={
              <SyncOutlined
                spin
                style={{ color: "var(--main-color)", fontSize: "24px" }}
              />
            }
          />
        </Flex>
      ) : (
        <SessionPrescription
          {...{
            state,
            form,
            medicinesLookup,
            handleChange,
            handleAddCustomMedicine,
            handleRemoveMedicine,
          }}
        />
      )}
      <div style={{ display: "none" }}>
        <ReadyToPrint
          name="Prescribed Medicines"
          print={printRef}
          Component={
            <div>
              <PrintForm
                {...{
                  state,
                  medicinesLookup,
                  sessions,
                }}
              />
            </div>
          }
        />
      </div>
    </Modal>
  );
};

export { ClientPrescriptionModal };
